import { KeyValue } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  InputSignal,
  model,
  OnDestroy,
  OnInit,
  output,
  signal,
  TrackByFunction,
  viewChild,
  WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { CheckboxComponent } from '@appkit4/angular-components/checkbox';
import {
  GridColumn,
  PaginationSetting,
  PrimeGridOption,
} from '@grid/abstractions';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationModalComponent } from '@shared/components/confirmation-modal/confirmation-modal.component';
import { paginationOptions } from '@shared/constants/pagination-settings';
import { TableDataSortType } from '@shared/enums/table-data-sort-type';
import { DataType } from '@shared/helpers/enums/data-type';
import { mapToGridRow } from '@shared/mappers/map-to-grid-row';
import { GridRow } from '@shared/models/grid/grid-row';
import { PrimeGridFilterFormComponent } from '@shared/modules/prime-grid-filters/components/prime-grid-filter-form/prime-grid-filter-form.component';
import { ModalService } from '@shared/services';
import { SortEvent } from 'primeng/api';
import { GridQueryBuilderService } from '../../services/grid-query-builder.service';
import { TableDataSortManagerService } from '../../services/table-data-sort-manager.service';
import { ODataQueryState } from '../../types/odata-query-state';

@Component({
  standalone: false,
  selector: 'gstar-prime-grid',
  templateUrl: './prime-grid.component.html',
  styleUrl: './prime-grid.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PrimeGridComponent<TData> implements OnInit, OnDestroy {
  private readonly tableDataSortManager = inject(TableDataSortManagerService);
  private readonly gridQueryBuilder = inject<GridQueryBuilderService<TData>>(
    GridQueryBuilderService
  );
  private readonly destroyRef = inject(DestroyRef);
  private readonly modalService = inject(ModalService);
  private readonly translateService = inject(TranslateService);
  private readonly explanationTooltipOverflowLength = 260;
  private readonly defaultSkeletonRows = 10;
  private readonly maxSkeletonRows = 20;

  headers = input<GridColumn[]>([]);
  source = model<TData[]>([]);
  isLoading = input(true);
  totalRows = input(0);
  sessionKey: InputSignal<string | undefined> = input();
  settings = input<PrimeGridOption<TData> | undefined>();
  hasButtons = computed(
    () => !!(this.settings() && this.settings()?.crudOptions)
  );
  hasFilters = computed(() => {
    return this.headers().some((header: GridColumn) => header.isFilterable);
  });
  skeletonRows = computed(() => {
    const rowByPage = Number(this.gridQueryBuilder.pagination().rowByPage);
    const rows =
      Number.isFinite(rowByPage) && rowByPage > 0
        ? rowByPage
        : this.defaultSkeletonRows;

    return Array.from({ length: Math.min(rows, this.maxSkeletonRows) });
  });

  queryChange = output<ODataQueryState<TData>>();
  selectRows = output<TData[]>();
  clearAll = output<void>();
  dataSource = signal<GridRow<TData>[]>([]);
  selectedAll: WritableSignal<boolean> = signal(false);
  extraColumnsCount: number = 2;
  options: KeyValue<string, number>[] = paginationOptions;
  pageChangedManually: WritableSignal<boolean> = signal(false);
  expandedRows: { [key: string]: boolean } = {};
  rowTrackBy: TrackByFunction<GridRow<TData>> = (
    _index: number,
    row: GridRow<TData>
  ) => row.uuid;

  filters = viewChild(PrimeGridFilterFormComponent);
  checkbox = viewChild<CheckboxComponent>('checkbox');

  constructor() {
    this.gridQueryBuilder.triggerQuery
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((query: ODataQueryState<TData>) => {
        this.selectRows.emit(this.getSelectedRows());
        this.queryChange.emit(query);
      });

    toObservable(this.source)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((source: TData[]) => {
        const gridData = mapToGridRow<TData>(source);
        gridData.forEach(item => (item.isChecked = this.selectedAll()));
        this.dataSource.set(gridData);
      });

    toObservable(this.totalRows)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((totalRows: number) => {
        this.gridQueryBuilder.updatePagination({ totalItems: totalRows }, true);
      });
  }

  ngOnDestroy(): void {
    if (!this.sessionKey()) {
      this.gridQueryBuilder.reset();
    }
  }

  ngOnInit(): void {
    this.gridQueryBuilder.initialize(
      this.sessionKey(),
      !!this.settings()?.extendedPagination
    );
  }

  get templateColumnType(): DataType {
    return DataType.Template;
  }

  isCellClickable(header: GridColumn): boolean {
    return !!header.onClick;
  }

  onCellClick(header: GridColumn, rowData: TData): void {
    header.onClick?.(rowData);
  }

  get pagination(): PaginationSetting {
    return this.gridQueryBuilder.pagination();
  }

  isTextTruncated(element: HTMLElement | undefined): boolean {
    return !!element && element.scrollWidth > element.clientWidth;
  }

  /**
   * Checks if the given value contains HTML content.
   * @param value The value to check for HTML content.
   * @returns True if the value contains HTML tags, false otherwise.
   */
  isHtmlContent(value: unknown): boolean {
    if (typeof value !== 'string') {
      return false;
    }

    return /<\/?[a-z][\s\S]*>/i.test(value);
  }

  /**
   * Sanitizes the given HTML content for use in tooltips.
   * @param value The HTML content to sanitize.
   * @returns The sanitized HTML content with inline styles removed.
   */
  sanitizeTooltipHtml(value: unknown): string {
    if (typeof value !== 'string') {
      return '';
    }

    // Remove inline styles and empty editor blocks so short text does not create tall tooltips.
    return value
      .replace(/\sstyle=("[^"]*"|'[^']*')/gi, '')
      .replace(
        /<(p|div|span|li|h[1-6])[^>]*>(?:\s|&nbsp;|<br\s*\/?\s*>)*<\/\1>/gi,
        ''
      )
      .trim();
  }

  shouldShowExplanationViewMore(value: unknown): boolean {
    if (typeof value !== 'string') {
      return false;
    }

    const normalizedText = value
      .replace(/<[^>]+>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    return normalizedText.length > this.explanationTooltipOverflowLength;
  }

  clearFilters(): void {
    if (this.filters()) {
      this.filters()?.clearFields();
    }
  }

  clearFiltersAndRun(): void {
    if (this.filters()) {
      this.filters()?.clearAndRun();
    }
  }

  clearSelection(): void {
    this.selectedAll.set(false);
    this.checkbox()?.writeValue(false);
    this.dataSource.update((rows: GridRow<TData>[]) =>
      rows.map((row: GridRow<TData>) => ({
        ...row,
        isChecked: false,
      }))
    );
    this.selectRows.emit([]);
  }

  emitClear(): void {
    this.clearAll.emit();
  }

  goToPage(currentPage: number): void {
    this.gridQueryBuilder.updatePagination({ currentPage });
  }

  sortTable(event: SortEvent): void {
    const column = this.headers().find(
      (column: GridColumn) => column.key === event.field
    );
    if (column) {
      column.sortType = event.order;

      let orderByValue: TableDataSortType;
      switch (event.order) {
        case 1:
          orderByValue = TableDataSortType.ASC;
          break;
        case -1:
          orderByValue = TableDataSortType.DESC;
          break;
        default:
          orderByValue = TableDataSortType.NONE;
      }

      this.gridQueryBuilder.updateOrder(column, orderByValue);

      if (this.settings() && !this.settings()?.lazy) {
        this.tableDataSortManager.sortJSONArray(this.source(), column);
      }
    }
  }

  openSingleModal(
    currentRow: GridRow<TData>,
    checkrow: CheckboxComponent
  ): void {
    if (this.selectedAll()) {
      checkrow.writeValue(true);
      this.modalService.open(
        ConfirmationModalComponent,
        {
          appModalId: 'selection-confirm-modal',
        },
        {
          title: this.translateService.instant(
            'portfolio.selectionModal.singleTitle'
          ),
          content: this.translateService.instant(
            'portfolio.selectionModal.singleContent'
          ),
          modalId: 'selection-confirm-modal',
          confirmationFn: () => {
            checkrow.writeValue(false);
            this.toggleSelect(currentRow);
          },
        }
      );
    } else {
      this.toggleSelect(currentRow);
    }
  }

  toggleSelect(currentRow: GridRow<TData>): void {
    const selectRowFn = (row: GridRow<TData>): GridRow<TData> => {
      if (row.uuid === currentRow.uuid) {
        row.isChecked = currentRow.isChecked;
      }
      return row;
    };

    this.toggleSelectRow(selectRowFn);
  }

  openSelectAllModal(): void {
    if (this.settings()?.disableSelectAllModal) {
      // Si el modal está deshabilitado, solo ejecutar toggleSelectAll
      this.toggleSelectAll();
    } else if (!this.selectedAll()) {
      this.checkbox()?.writeValue(false);
      this.modalService.open(
        ConfirmationModalComponent,
        {
          appModalId: 'selection-confirm-modal',
        },
        {
          title: this.translateService.instant(
            'portfolio.selectionModal.allTitle'
          ),
          content: this.translateService.instant(
            'portfolio.selectionModal.allContent',
            { count: this.totalRows() }
          ),
          modalId: 'selection-confirm-modal',
          confirmationFn: () => {
            this.checkbox()?.writeValue(true);
            this.toggleSelectAll();
          },
        }
      );
    } else {
      this.toggleSelectAll();
    }
  }

  toggleSelectAll(value?: boolean): void {
    this.selectedAll.set(value !== undefined ? value : !this.selectedAll());
    const selectAllFn = (row: GridRow<TData>): GridRow<TData> => {
      row.isChecked = this.selectedAll();
      return row;
    };

    this.toggleSelectRow(selectAllFn);
  }

  pageChange(pagination: PaginationSetting): void {
    if (this.selectedAll()) {
      this.pageChangedManually.set(true);
    }
    this.gridQueryBuilder.updatePagination(pagination);
  }

  filterChange(filter: ODataQueryState<TData>): void {
    this.gridQueryBuilder.updatePagination({ currentPage: 1 }, true);
    this.gridQueryBuilder.updateFilter(filter);
  }

  private getSelectedRows(): TData[] {
    return this.dataSource()
      .filter((row: GridRow<TData>) => row.isChecked)
      .map((row: GridRow<TData>) => row.data);
  }

  private toggleSelectRow(
    filterFn: (row: GridRow<TData>) => GridRow<TData>
  ): void {
    const rows = this.dataSource().map(filterFn);
    if (this.getSelectedRows().length !== rows.length && this.selectedAll()) {
      this.selectedAll.set(false);
    }
    this.dataSource.set(rows);
    this.selectRows.emit(this.getSelectedRows());
  }

  private toggleRowExpansion(rowIndex: string | number): void {
    this.expandedRows[rowIndex] = !this.expandedRows[rowIndex];
  }
}
